import { useRef, useState, useCallback } from 'react';
import { CreditCard, Wifi, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const TIER_CLASSES = {
  'physical-grey': 'card3d--physical-grey',
  'physical-gold': 'card3d--physical-gold',
  'physical-business': 'card3d--physical-business',
  'virtual-grey': 'card3d--virtual-grey',
  'virtual-silver': 'card3d--virtual-silver',
  'virtual-platinum': 'card3d--virtual-platinum',
};

const TIER_LABELS = {
  'physical-grey': 'GREY',
  'physical-gold': 'GOLD',
  'physical-business': 'BUSINESS',
  'virtual-grey': 'GREY',
  'virtual-silver': 'SILVER',
  'virtual-platinum': 'PLATINUM',
};

export function Card3D({ tier = 'grey', variant = 'physical', cardNumber, holderName, expiry, className, mini = false }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const key = `${variant}-${tier}`;
  const tierClass = TIER_CLASSES[key] || 'card3d--physical-grey';
  const tierLabel = TIER_LABELS[key] || tier.toUpperCase();

  const onMove = useCallback((e) => {
    if (!ref.current || mini) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ rx: (y * -10).toFixed(2), ry: (x * 12).toFixed(2) });
    setIsHovered(true);
  }, [mini]);

  const onLeave = useCallback(() => {
    setTilt({ rx: 0, ry: 0 });
    setIsHovered(false);
  }, []);

  return (
    <div
      ref={ref}
      className={cn('card3d-wrap', className)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`cards-3d-card-${variant}-${tier}`}
    >
      <div
        className={cn('card3d', tierClass, isHovered && 'card3d--hovered')}
        style={{
          '--rx': `${tilt.rx}deg`,
          '--ry': `${tilt.ry}deg`,
        }}
      >
        <div className="card3d__shine" />
        <div className="card3d__content">
          {/* Top Row */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5">
              <CreditCard className="w-5 h-5 opacity-80" />
              <span className="text-xs font-semibold tracking-wider opacity-90">Safe Crypto</span>
            </div>
            <div className="flex items-center gap-1.5">
              {variant === 'virtual' && <Wifi className="w-4 h-4 opacity-60" />}
              <ShieldCheck className="w-4 h-4 opacity-60" />
            </div>
          </div>

          {/* Card Chip */}
          {variant === 'physical' && (
            <div className="card3d__chip">
              <div className="card3d__chip-lines" />
            </div>
          )}

          {variant === 'virtual' && (
            <div className="card3d__virtual-indicator">
              <div className="card3d__virtual-dots">
                <span /><span /><span />
              </div>
            </div>
          )}

          {/* Card Number */}
          <div className="mt-auto">
            <p className="font-mono text-sm tracking-[0.2em] opacity-90">
              {cardNumber || '**** **** **** ****'}
            </p>
          </div>

          {/* Bottom Row */}
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[9px] uppercase tracking-wider opacity-50 mb-0.5">Card Holder</p>
              <p className="text-xs font-medium tracking-wide opacity-80">
                {holderName || 'YOUR NAME'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-wider opacity-50 mb-0.5">Expires</p>
              <p className="text-xs font-mono opacity-80">{expiry || 'MM/YY'}</p>
            </div>
          </div>

          {/* Tier Badge */}
          <div className="card3d__tier-badge">
            <span className="text-[9px] font-bold tracking-[0.3em]">{tierLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
